// src/components/Apm/Profile/AccountProfileDetails.js

import { useEffect, useMemo, useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import {
  Card,
  CardContent,
  Typography,
  Box,
  CardHeader,
  Divider,
  Grid,
  CardActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { setPopup } from '../../../redux/slices/popupSlice';
import { setFormSimpleTouched } from '../../../redux/slices/formTouchedSlice';
import {
  useUpdateCurrentUserMutation,
  useUpdateCompanyUserMutation,
  useDeleteUserMutation,
  useLogoutMutation,
} from '../../../redux/features/authApi';
import ButtonAction from '../../Controls/Buttons/ButtonAction';
import ButtonLoading from '../../Controls/Buttons/ButtonLoading';
import ConfirmDialog from '../../Controls/Popups/ConfirmDialog';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import { initialValuesAccount } from '../Tables/InitialValues/initialValuesAccount';
import { Stack } from '@mui/system';

const validationSchema = Yup.object({
  first_name: Yup.string()
    .max(250, () => i18n.t('yup.max_250_characters'))
    .nullable(),

  last_name: Yup.string()
    .max(250, () => i18n.t('yup.max_250_characters'))
    .nullable(),

  phone: Yup.string()
    .max(100, () => i18n.t('yup.max_100_characters'))
    .nullable(),

  skype: Yup.string()
    .max(100, () => i18n.t('yup.max_100_characters'))
    .nullable(),
});

/**
 * Keeps the existing unsaved-form warning mechanism, but uses Formik's
 * `dirty` state rather than manually copying every form value into state.
 */
const FormStateTracker = ({ enabled }) => {
  const dispatch = useDispatch();
  const { dirty } = useFormikContext();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(setFormSimpleTouched(dirty));
  }, [dispatch, dirty, enabled]);

  return null;
};

const getErrorStatus = (error) => {
  return error?.status ?? error?.originalStatus ?? error?.response?.status ?? null;
};

export const AccountProfileDetails = ({ account, itemForEdit = null, mode = 'account' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const isUsersPage = mode === 'user-management';

  const isAccountPage = mode === 'account';

  const [edit, setEdit] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
    type: '',
  });

  const [updateCurrentUser, { isLoading: isUpdatingCurrentUser }] = useUpdateCurrentUserMutation();

  const [updateCompanyUser, { isLoading: isUpdatingCompanyUser }] = useUpdateCompanyUserMutation();

  const isUpdating = isUpdatingCurrentUser || isUpdatingCompanyUser;

  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  const sourceUser = isUsersPage ? itemForEdit : account;

  const initialValues = useMemo(
    () => ({
      ...initialValuesAccount,
      ...(sourceUser ?? {}),
    }),
    [sourceUser]
  );

  const mutationLoading = isUpdating || isDeleting || isLoggingOut;

  useEffect(() => {
    setEdit(false);

    if (isUsersPage) {
      dispatch(setFormSimpleTouched(false));
    }
  }, [dispatch, isUsersPage, itemForEdit?.id]);

  const handleSubmit = async (values, { resetForm }) => {
    if (!values?.id) {
      toast.error(() => t('toast.record_not_found'));

      return;
    }

    const body = {
      first_name: values.first_name || '',

      last_name: values.last_name || '',

      phone: values.phone || '',

      skype: values.skype || '',
    };

    try {
      const isOwnProfile = Boolean(account?.id) && String(account.id) === String(values.id);

      const updatedUser = isOwnProfile
        ? await updateCurrentUser(body).unwrap()
        : await updateCompanyUser({
            id: values.id,
            body,
          }).unwrap();

      resetForm({
        values: {
          ...initialValuesAccount,
          ...updatedUser,
        },
      });

      setEdit(false);

      if (isUsersPage) {
        dispatch(setPopup(false));

        dispatch(setFormSimpleTouched(false));
      }

      toast.success(`${t('toast.profile')} ${t('toast.successfully_updated')}`);
    } catch (error) {
      console.error('User update failed:', error);

      const status = getErrorStatus(error);

      if (status === 400) {
        console.error('Validation response:', error?.data);

        toast.error(() => t('toast.not_saved_try_again'), {
          autoClose: 4000,
        });

        return;
      }

      if (status === 401 || status === 403) {
        toast.error(() => t('toast.only_account_manager_allowed'), {
          autoClose: 4000,
        });

        return;
      }

      if (status === 404) {
        toast.error(() => t('toast.record_not_found'), {
          autoClose: 4000,
        });

        return;
      }

      toast.error(() => t('toast.not_saved_try_again'), {
        autoClose: 4000,
      });
    }
  };

  const handleDelete = async (values) => {
    const userId = values?.id;

    if (!userId) {
      toast.error(() => t('toast.record_not_found'), {
        autoClose: 4000,
      });

      return;
    }

    const deletingOwnAccount = String(account?.id) === String(userId);

    try {
      await deleteUser(userId).unwrap();

      dispatch(setPopup(false));
      dispatch(setFormSimpleTouched(false));

      if (deletingOwnAccount) {
        /*
         * The user record is already deleted. Logout is still called so
         * the backend can remove the HttpOnly authentication cookies.
         */
        try {
          await logout().unwrap();
        } catch (logoutError) {
          console.error('Cookie logout after account deletion failed:', logoutError);
        }

        if (deletingOwnAccount) {
          try {
            await logout().unwrap();
          } catch (logoutError) {
            console.warn('Cookie logout after account deletion failed:', logoutError);
          } finally {
            navigate('/', {
              replace: true,
            });
          }
        }

        navigate('/', {
          replace: true,
        });
      }

      toast.success(() => t('toast.record_successfully_deleted'));
    } catch (error) {
      console.error('User deletion failed:', error);

      const status = getErrorStatus(error);

      if (status === 401 || status === 403) {
        toast.error(() => t('toast.only_account_manager_allowed'), {
          autoClose: 4000,
        });

        return;
      }

      toast.error(() => t('toast.not_deleted_try_again'), {
        autoClose: 4000,
      });
    }
  };

  return (
    <>
      <Formik
        enableReinitialize={!edit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, resetForm, dirty }) => {
          const isOwnProfile = Boolean(account?.id) && String(account.id) === String(values?.id);

          const canManageUser = Boolean(account?.is_manager);

          const canEdit = isOwnProfile || canManageUser;

          const handleCancel = () => {
            resetForm({
              values: initialValues,
            });

            setEdit(false);

            if (isUsersPage) {
              dispatch(setFormSimpleTouched(false));
            }
          };
          const openDeleteConfirmation = (event) => {
            event.preventDefault();

            setConfirmDialog({
              isOpen: true,

              title: t('popup.are_you_sure_to_delete_account'),

              subTitle: (
                <>
                  <div>{t('popup.you_can_not_undo_this_operation')}</div>

                  {account?.is_manager && isOwnProfile && (
                    <div>{t('popup.additional_users_and_related_entries_will_be_deleted')}</div>
                  )}
                </>
              ),

              onConfirm: async () => {
                setConfirmDialog((current) => ({
                  ...current,
                  isOpen: false,
                }));

                await handleDelete(values);
              },
            });
          };

          return (
            <Form autoComplete="off" noValidate>
              <Card elevation={isUsersPage ? 0 : undefined}>
                {isAccountPage && (
                  <>
                    <CardHeader
                      title={
                        <Grid container item>
                          <Grid item xs={6}>
                            <Typography variant="h6">{t('general.profile')}</Typography>
                          </Grid>

                          <Grid item xs={4} />

                          <Grid container item xs={2} justifyContent="flex-end">
                            <ButtonAction type="button" color="error" disabled={!edit} onClick={handleCancel}>
                              <Typography variant="button">{t('button.cancel')}</Typography>
                            </ButtonAction>
                          </Grid>
                        </Grid>
                      }
                      sx={{
                        backgroundColor: '#f1f1f1',
                      }}
                    />

                    <Divider />
                  </>
                )}

                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item md={6} xs={12}>
                      <TextFieldNormal
                        fullWidth
                        label={t('person.first_name')}
                        name="first_name"
                        variant="outlined"
                        disabled={!edit}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextFieldNormal
                        fullWidth
                        label={t('person.last_name')}
                        name="last_name"
                        variant="outlined"
                        disabled={!edit}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextFieldNormal
                        fullWidth
                        label={t('person.email_can_not_be_changed')}
                        name="email"
                        variant="outlined"
                        disabled
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextFieldNormal
                        fullWidth
                        label={t('general.phone')}
                        name="phone"
                        variant="outlined"
                        disabled={!edit}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextFieldNormal
                        fullWidth
                        label={t('general.skype')}
                        name="skype"
                        type="text"
                        variant="outlined"
                        disabled={!edit}
                      />
                    </Grid>
                  </Grid>
                </CardContent>

                <Divider />

                <CardActions
                  sx={{
                    mt: 3,
                    mb: 3,
                    ml: 1,
                    mr: 1,
                  }}
                >
                  {canEdit && (
                    <Grid container item>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent={isUsersPage ? 'flex-end' : 'flex-start'}
                        width="100%"
                      >
                        <ButtonLoading
                          type="button"
                          color="primary"
                          variant="outlined"
                          size="small"
                          fullWidth={smMediaDown || undefined}
                          disabled={edit || mutationLoading}
                          onClick={() => setEdit(true)}
                        >
                          <Typography variant="button">{t('button.edit_profile')}</Typography>
                        </ButtonLoading>

                        <Box
                          display="flex"
                          justifyContent={isUsersPage ? 'flex-end' : 'flex-start'}
                          alignItems="flex-end"
                          m={0}
                        >
                          <ButtonLoading
                            type="submit"
                            color="primary"
                            variant="outlined"
                            loading={isUpdating}
                            size="small"
                            fullWidth={smMediaDown || undefined}
                            disabled={!edit || !dirty || mutationLoading}
                            sx={{ mr: 0 }}
                          >
                            <Typography variant="button">
                              {isUsersPage ? t('button.save_and_close') : t('button.save')}
                            </Typography>
                          </ButtonLoading>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                </CardActions>
              </Card>

              {canEdit && (
                <Card elevation={isUsersPage ? 0 : undefined} sx={{ mt: 4 }}>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="error">
                          {t('account_page.delete_account_text_1')}
                        </Typography>

                        <Typography variant="h6" color="error">
                          {t('account_page.delete_account_text_2')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Divider />

                  <CardActions
                    sx={{
                      mt: 3,
                      mb: 3,
                      ml: 1,
                      mr: 1,
                    }}
                  >
                    <Grid item xs={12}>
                      <ButtonLoading
                        type="button"
                        variant="outlined"
                        size="small"
                        color="error"
                        fullWidth={smMediaDown || undefined}
                        loading={isDeleting || isLoggingOut}
                        disabled={mutationLoading}
                        onClick={openDeleteConfirmation}
                      >
                        <Typography variant="button">{t('button.delete_account')}</Typography>
                      </ButtonLoading>
                    </Grid>
                  </CardActions>
                </Card>
              )}

              <FormStateTracker enabled={isUsersPage} />
            </Form>
          );
        }}
      </Formik>

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
};
